import React from "react";
import { CircularProgress, Tag } from "@chakra-ui/react";
import ContinueButton from "../Buttons/ContinueButton";
import { CourseLevel } from "@shared_types";
import { HiMiniCheckBadge } from "react-icons/hi2";
import PressableCard from "@/ui/PressableCard";
import { haptic } from "@/lib/haptic";
import { getProgressColors, getLevelColor } from "../../styles/courseStyles";
import { getTagStyles } from "../../styles/cardStyles";
import CourseIcon from "./CourseIcon";

interface SquareCardProps {
  emoji: string | React.ReactNode;
  title: string;
  selected?: boolean;
  subtitle?: string;
  onClick?: () => void;
  completion?: number;
  level?: CourseLevel;
  progress?: number;
  chapters?: string;
}

const CoursesCardSquare: React.FC<SquareCardProps> = ({
  emoji,
  title,
  selected = false,
  subtitle = "",
  onClick,
  level,
  progress,
}) => {
  const { progressColor, progressTrackColor } = getProgressColors(progress);
  const levelColor = getLevelColor(level);

  const handlePress = () => {
    haptic.soft();
    onClick?.();
  };

  return (
    <PressableCard
      as="div"
      onPress={handlePress}
      selected={selected}
      padding={16}
      containerStyle={{
        flex: 1,
        minWidth: "var(--large-card-width)",
        maxWidth: "var(--large-card-width)",
      }}
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "flex-start",
        overflow: "hidden",
      }}
    >
      <div className="flex absolute top-[22px] right-[22px] items-end">
        {(() => {
          if (progress !== undefined && progress === 100) {
            return (
              <HiMiniCheckBadge
                className="text-[var(--color-green)]"
                size={42}
              />
            );
          } else if (progress !== undefined && progress !== 0) {
            return (
              <CircularProgress
                value={progress}
                color={progressColor}
                trackColor={progressTrackColor}
                size={"40px"}
                thickness={"16px"}
              />
            );
          } else {
            return null;
          }
        })()}
      </div>
      <div
        className="pl-1 flex items-center"
        style={{ fontSize: "var(--base-font-size-6xlarge)" }}
      >
        {typeof emoji === "string" ? (
          <CourseIcon emoji={emoji} size="large" />
        ) : (
          emoji
        )}
      </div>
      <div className="flex flex-col w-full gap-1">
        <Tag {...getTagStyles("level")} color={levelColor}>
          {level?.name?.toUpperCase()}
        </Tag>
        <div className="flex flex-row justify-start items-center gap-4">
          <div className="font-bold text-2xl text-left">{title}</div>
        </div>

        <div className="text-[var(--color-grey)] text-sm flex-grow text-left mb-2">
          {subtitle}
        </div>
        <div onClick={(e) => e.stopPropagation()} className="w-full">
          <ContinueButton
            className="w-full"
            text="Continue"
            onClick={handlePress}
          />
        </div>
      </div>
    </PressableCard>
  );
};

export default CoursesCardSquare;
